import { signal } from '@preact/signals';
import { tokenSignal } from '../state/auth.store';
import { activeContactSignal, loadContacts, loadContactDetails, activeSummaryJobSignal, syncingContactsSignal, syncHistoryProgressSignal } from '../state/contacts.store';
import { appendMessage, updateMessageExtractedText, updateMessageReaction } from '../state/messages.store';
import { showToast } from '../state/ui.store';
import { apiFetch } from './api';
import type { Message } from '../types/message';

export const sseConnectionStatusSignal = signal<'connecting' | 'connected' | 'disconnected'>('connecting');

let sseAbortController: AbortController | null = null;
let reconnectTimer: any = null;
let watchdogTimer: any = null;

export function startSummaryJobWatchdog(phone: string) {
  stopSummaryJobWatchdog();

  watchdogTimer = setInterval(async () => {
    const job = activeSummaryJobSignal.value;
    if (!job) {
      stopSummaryJobWatchdog();
      return;
    }

    try {
      const res = await apiFetch<{ job?: any | null }>(`/ai/summarize/${phone}/job`).catch(() => ({ job: null }));
      if (!res?.job || !['TRANSCRIBING', 'GENERATING_SUMMARY', 'EXTRACTING_ACTIONS'].includes(res.job.status)) {
        stopSummaryJobWatchdog();
        activeSummaryJobSignal.value = null;

        const activeContact = activeContactSignal.value;
        if (activeContact && activeContact.phone_number === phone) {
          showToast('✓ Resumo atualizado com sucesso!', 'success');
          loadContactDetails(activeContact);
        }
      } else {
        activeSummaryJobSignal.value = res.job;
      }
    } catch (e) {
      // Ignorar erros transientes de rede durante o watchdog
    }
  }, 3500);
}

export function stopSummaryJobWatchdog() {
  if (watchdogTimer) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
}

export async function cancelActiveSummaryJob(phone: string) {
  try {
    stopSummaryJobWatchdog();
    await apiFetch(`/ai/summarize/${phone}/job`, { method: 'DELETE' }).catch(() => {});
    activeSummaryJobSignal.value = null;
    showToast('Job de resumo cancelado / destravado com sucesso.', 'info');
    const activeContact = activeContactSignal.value;
    if (activeContact && activeContact.phone_number === phone) {
      loadContactDetails(activeContact);
    }
  } catch (err: any) {
    console.error('[SSE] Erro ao cancelar job:', err);
    activeSummaryJobSignal.value = null;
  }
}

export async function initRealtimeSync() {
  const token = tokenSignal.value;
  if (!token) return;

  if (sseAbortController) {
    sseAbortController.abort();
    sseAbortController = null;
  }

  sseAbortController = new AbortController();
  sseConnectionStatusSignal.value = 'connecting';

  try {
    const response = await fetch('/api/events/stream', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream'
      },
      signal: sseAbortController.signal
    });

    if (!response.ok || !response.body) {
      throw new Error(`HTTP ${response.status}`);
    }

    console.log('[SSE Realtime] Conexão SSE estabelecida com sucesso.');
    sseConnectionStatusSignal.value = 'connected';
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';

      for (const block of blocks) {
        for (const line of block.split('\n')) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleRealtimePayload(data);
            } catch (err) {
              console.warn('[SSE Realtime] Erro ao parsear payload:', err);
            }
          }
        }
      }
    }
  } catch (err: any) {
    if (sseAbortController?.signal.aborted) return;
    sseConnectionStatusSignal.value = 'disconnected';
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      initRealtimeSync();
    }, 3000);
  }
}

function handleRealtimePayload(payload: any) {
  if (!payload || !payload.type) return;

  const { type, contactPhone, message, messageId, extractedText, targetMessageId, reaction } = payload;
  const activeContact = activeContactSignal.value;

  if (type === 'new_message' && message) {
    loadContacts();

    if (activeContact && (activeContact.phone_number === contactPhone || (contactPhone && contactPhone.includes(activeContact.phone_number)))) {
      appendMessage(message as Message);
    }
  } else if (type === 'reaction_updated' && targetMessageId && reaction) {
    if (activeContact) {
      updateMessageReaction(targetMessageId, reaction);
    }
  } else if (type === 'media_transcribed' && messageId) {
    if (activeContact) {
      updateMessageExtractedText(messageId, extractedText);
    }
  } else if (type === 'summary_progress') {
    if (activeContact && activeContact.phone_number === payload.contactPhone) {
      activeSummaryJobSignal.value = payload;
      startSummaryJobWatchdog(payload.contactPhone);
    }
  } else if (type === 'summary_completed') {
    stopSummaryJobWatchdog();
    if (activeContact && activeContact.phone_number === payload.contactPhone) {
      activeSummaryJobSignal.value = null;
      showToast('✓ Resumo executivo atualizado com sucesso!', 'success');
      loadContactDetails(activeContact);
    }
  } else if (type === 'summary_failed') {
    stopSummaryJobWatchdog();
    if (activeContact && activeContact.phone_number === payload.contactPhone) {
      activeSummaryJobSignal.value = null;
      showToast(`Erro no resumo: ${payload.error || 'Falha no processamento.'}`, 'error');
    }
  } else if (type === 'sync_history_started' && contactPhone) {
    syncingContactsSignal.value = { ...syncingContactsSignal.value, [contactPhone]: true };
    syncHistoryProgressSignal.value = {
      ...syncHistoryProgressSignal.value,
      [contactPhone]: { progress: payload.progress || 5, totalSynced: 0, audiosTranscribed: 0 }
    };
  } else if (type === 'sync_history_progress' && contactPhone) {
    syncingContactsSignal.value = { ...syncingContactsSignal.value, [contactPhone]: true };
    syncHistoryProgressSignal.value = {
      ...syncHistoryProgressSignal.value,
      [contactPhone]: {
        progress: payload.progress || 0,
        totalSynced: payload.totalSynced || 0,
        audiosTranscribed: payload.audiosTranscribed || 0
      }
    };
  } else if (type === 'sync_history_completed' && contactPhone) {
    const updatedSync = { ...syncingContactsSignal.value };
    delete updatedSync[contactPhone];
    syncingContactsSignal.value = updatedSync;

    const updatedProg = { ...syncHistoryProgressSignal.value };
    delete updatedProg[contactPhone];
    syncHistoryProgressSignal.value = updatedProg;

    showToast(payload.message || 'Sincronização de histórico concluída com sucesso!', 'success');
    if (activeContact && activeContact.phone_number === contactPhone) {
      loadContactDetails(activeContact);
    }
  } else if (type === 'sync_history_failed' && contactPhone) {
    const updatedSync = { ...syncingContactsSignal.value };
    delete updatedSync[contactPhone];
    syncingContactsSignal.value = updatedSync;

    syncHistoryProgressSignal.value = {
      ...syncHistoryProgressSignal.value,
      [contactPhone]: {
        progress: 0,
        totalSynced: 0,
        audiosTranscribed: 0,
        status: 'FAILED',
        error: payload.error || 'Falha na sincronização.'
      } as any
    };

    showToast(`⚠️ Erro na sincronização: ${payload.error || 'Falha no processamento.'}`, 'error');
  } else if (type === 'contacts_updated') {
    loadContacts();
    if (activeContact) {
      loadContactDetails(activeContact);
    }
  }
}
